import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { searchMyRuns, SearchMyRunsRequest } from '@neptune/home-page-domain';
import { useNameSearchQuery } from '@neptune/object-table-search-domain';
import { SearchQuery, SearchQueryModelConverter } from '@neptune/search-query-domain';
import {
  usePolling,
  UsePollingParams,
  useUnderEditContext,
} from '@neptune/shared/common-business-logic';
import { Entity } from '@neptune/shared/entity-domain';
import { LeaderboardContext } from '@neptune/shared/entity-leaderboard-domain';
import {
  fetchLeaderboard,
  fetchLeaderboardAbort,
  fetchLeaderboardFail,
  fetchLeaderboardSuccess,
  getLeaderboardState,
  useExperimentsModeContext,
  useLeaderboardPagination,
  useSortOptions,
} from '@neptune/shared/leaderboard-business-logic';
import { LeaderboardListModel } from '@neptune/shared/leaderboard-domain';
import { NameSearchMode } from '@neptune/shared/search-domain';

import { KnownAttributes } from 'domain/experiment/attribute';
import { AppState } from 'state/types';

import { useUsernameAndMyFilterSearchQuery } from './use-username-and-my-filter-search-query';

const nonArchivedQuery: SearchQuery = {
  criteria: [
    {
      attribute: KnownAttributes.Archived,
      operator: '=',
      type: 'bool',
      value: false,
    },
  ],
  operator: 'and',
};

export const useMyRunsPolling = () => {
  const dispatch = useDispatch();
  const { id } = React.useContext(LeaderboardContext);
  const { searchQuery, searchQueryConvertedToLegacyName, mode } = useNameSearchQuery();
  const { usernameAndMyRunsFilterQuery } = useUsernameAndMyFilterSearchQuery();

  const properSearchQuery =
    mode === NameSearchMode.REGEX ? searchQuery : searchQueryConvertedToLegacyName;

  const query = React.useMemo(
    () =>
      SearchQueryModelConverter.convertSearchQueryToNql({
        criteria: [
          nonArchivedQuery,
          properSearchQuery,
          ...(usernameAndMyRunsFilterQuery ? [usernameAndMyRunsFilterQuery] : []),
        ],
        operator: 'and',
      }),
    [properSearchQuery, usernameAndMyRunsFilterQuery],
  );

  const resolver = React.useCallback(async (request: SearchMyRunsRequest) => {
    return await searchMyRuns(request);
  }, []);

  const [sorting] = useSortOptions();
  const { pagination } = useLeaderboardPagination(id);
  const { experimentsOnlyEnabled: experimentsOnly } = useExperimentsModeContext();

  const searchParams: SearchMyRunsRequest = React.useMemo(
    () => ({
      query,
      sorting,
      pagination,
      experimentsOnly,
    }),
    [query, sorting, pagination, experimentsOnly],
  );

  const { underEdit } = useUnderEditContext();

  const handleFetchSuccess = React.useCallback(
    (data: LeaderboardListModel<Entity>, query: SearchMyRunsRequest) =>
      dispatch(fetchLeaderboardSuccess(id, { data, query })),
    [dispatch, id],
  );

  const handleFetchFailed = React.useCallback(
    (error: any) => dispatch(fetchLeaderboardFail(id, error)),
    [dispatch, id],
  );

  const handleFetchStart = React.useCallback(() => dispatch(fetchLeaderboard(id)), [dispatch, id]);

  const handleFetchAborted = React.useCallback(
    () => dispatch(fetchLeaderboardAbort(id)),
    [dispatch, id],
  );

  const pollingConfig: UsePollingParams<
    SearchMyRunsRequest,
    LeaderboardListModel<Entity>
  > = React.useMemo(
    () => ({
      resolver,
      onStart: handleFetchStart,
      onFetchSucceed: handleFetchSuccess,
      onFetchFailed: handleFetchFailed,
      onFetchAborted: handleFetchAborted,
      isPaused: underEdit,
    }),
    [
      resolver,
      handleFetchStart,
      handleFetchSuccess,
      handleFetchFailed,
      handleFetchAborted,
      underEdit,
    ],
  );

  const retryConfig = React.useMemo(() => ({ initialInterval: 400 }), []);

  const { refresh } = usePolling(searchParams, pollingConfig, retryConfig);

  const state = useSelector((state: AppState) => getLeaderboardState<Entity>(state, id));

  return {
    ...state,
    refresh,
  };
};
